import type { CardId, Suit } from './cards.js';

export type Seat = 0 | 1 | 2;
export const SEATS: readonly Seat[] = [0, 1, 2];

/**
 * Bir elde bir oyuncunun almak ZORUNDA olduğu el sayısı. Rolün adı hedefin
 * kendisi: 8'ci 8 el, 5'ci 5 el, 3'cü 3 el. Toplam 16 — yani puanlar her
 * elde birbirini götürüyor (sıfır toplamlı).
 */
export type Target = 8 | 5 | 3;
export const TARGETS: readonly Target[] = [8, 5, 3];

/** Bir elde toplam kaç tur oynanır. */
export const TRICKS_PER_DEAL = 16;
/** Yerdeki kapalı kart sayısı — 8'cinin gömdüğü kart sayısıyla aynı. */
export const KITTY_SIZE = 4;

/**
 * Koltuğun bu eldeki hedefi.
 *
 * Sıra 8 → 5 → 3 yönünde ilerliyor (koltuk numarası +1), yani 8'cinin bir
 * sonraki koltuğu 5'ci, ondan sonraki 3'cü. Rol dönüşü de aynı yönde:
 * her el 8'cilik bir sonraki koltuğa geçiyor (5 → 8, 8 → 3, 3 → 5).
 */
export function targetOf(role8: Seat, seat: Seat): Target {
  const step = (seat - role8 + 3) % 3;
  return step === 0 ? 8 : step === 1 ? 5 : 3;
}

/** Bu hedefe sahip koltuk. `targetOf`'un tersi. */
export function seatWithTarget(role8: Seat, target: Target): Seat {
  const step = target === 8 ? 0 : target === 5 ? 1 : 2;
  return ((role8 + step) % 3) as Seat;
}

export interface TrickPlay {
  seat: Seat;
  card: CardId;
}

export interface Trick {
  /** Turu açan koltuk — ilk turda 8'ci, sonrakilerde önceki turu alan. */
  leader: Seat;
  plays: TrickPlay[];
}

export type HistoryEntry =
  | { kind: 'play'; seat: Seat; card: CardId }
  | { kind: 'trickEnd'; winner: Seat; tricks: number[] };

/**
 * Bir elin akışı dört fazdan geçiyor:
 *
 *   exchange → trump → bury → play
 *
 * `exchange` yalnızca set başı OLMAYAN ve önceki elde artı/eksi çıkan
 * ellerde var. `trump` ve `bury` her elde var ve ikisi de 8'ciye ait.
 */
export type Phase = 'exchange' | 'trump' | 'bury' | 'play' | 'matchOver';

export interface Deal {
  hands: CardId[][];
  /**
   * Yerdeki 4 kapalı kart. 8'ci gömmesini yaptıktan SONRA bunlar eline
   * geçiyor — yani gömerken içeriğini bilmiyor.
   */
  kitty: CardId[];
  /** 8'cinin gömdüğü 4 kart. El boyunca oyun dışı ve gizli. */
  buried: CardId[];
  trump: Suit | null;
  /** Bu elin 8'cisi. Hedefler bundan türüyor (`targetOf`). */
  role8: Seat;
  turn: Seat;
  trick: Trick;
  /** Koltuk başına alınan el sayısı. Toplamı 16 olunca el biter. */
  tricks: number[];
  /** Tamamlanan tur sayısı. */
  trickCount: number;
  /**
   * Bu elde oynanmış tüm kartlar. Herkesin gördüğü bilgi — hem bot hem UI
   * "bu renkten kaç kart kaldı", "elimdeki K en büyüğü mü" sorularını buradan
   * cevaplıyor. Ayrı tutuluyor çünkü `history` içinden her seferinde süzmek
   * bot simülasyonunda ölçülebilir maliyet.
   */
  played: CardId[];
  history: HistoryEntry[];
}

/* ------------------------------------------------------------------ *
 * El sonu kart alışverişi
 * ------------------------------------------------------------------ */

/**
 * Bekleyen bir alışveriş adımı: artıda biten bir oyuncunun, eksideki
 * oyuncu(lar)dan kaç kart isteyeceği.
 *
 * İki isteyen varsa kuyruk ÖNCELİK SIRASINDA: önce o setin puanı yüksek olan,
 * eşitlikte oynanacak elin rolü büyük olan (8 > 5 > 3). Sıra önemli çünkü
 * ilk isteyen karşı tarafın elini değiştiriyor.
 *
 * Bir isteyenin birden fazla hedefi olabilir (iki kişi de eksideyse); o
 * durumda ikisine de AYNI ANDA veriliyor ve dönen kartlar birlikte görülüyor.
 */
export interface ExchangeStep {
  from: Seat;
  gives: { to: Seat; count: number }[];
}

/** Tamamlanmış bir alışveriş — iki tarafın da gördüğü kayıt. */
export interface ExchangeTransfer {
  /** İsteyen (artıda biten). */
  from: Seat;
  to: Seat;
  /** İsteyenin verdiği kartlar (kendi seçimi). */
  given: CardId[];
  /** Karşılığında zorunlu olarak dönen kartlar. */
  received: CardId[];
}

export interface Exchange {
  /** Sırada bekleyen adımlar; `queue[0]` şu an oynanan. */
  queue: ExchangeStep[];
  done: ExchangeTransfer[];
}

/* ------------------------------------------------------------------ *
 * Maç
 * ------------------------------------------------------------------ */

export interface MatchConfig {
  /**
   * Bir sette kaç el oynanır — alışveriş de bu sınırda sıfırlanıyor.
   * Set başında kimse önceki setin borcuyla başlamıyor.
   */
  dealsPerSet: number;
  /** Maçta kaç set oynanır. Toplam el = dealsPerSet * sets. */
  sets: number;
  /** Sıra başına saniye. */
  turnSeconds: number;
}

export const DEFAULT_CONFIG: MatchConfig = {
  dealsPerSet: 4,
  sets: 3,
  turnSeconds: 15,
};

/** Biten bir elin kaydı — puan çizelgesinin kaynağı. */
export interface DealResult {
  role8: Seat;
  trump: Suit;
  /** Koltuk başına alınan el sayısı. */
  tricks: number[];
  /** Koltuk başına hedeften sapma (alınan − hedef). Toplamı her zaman 0. */
  deltas: number[];
  /** El bittikten sonraki kümülatif puanlar. */
  scores: number[];
}

export interface Match {
  config: MatchConfig;
  seed: string;
  phase: Phase;
  /** 0'dan başlar; dealsPerSet * sets olunca maç biter. */
  dealIndex: number;
  scores: number[];
  /**
   * Maçın ilk 8'cisi — karo 2 kimdeyse o. Sonraki her elin 8'cisi buradan
   * türüyor: `(firstRole8 + dealIndex) % 3`. Ayrıca saklanmıyor.
   */
  firstRole8: Seat;
  deal: Deal | null;
  exchange: Exchange | null;
  /** Tamamlanan her elin kaydı, eskiden yeniye. */
  dealResults: DealResult[];
  /**
   * Biten elin tamamı (geçmişiyle birlikte). El bitince `deal` yeni ele
   * geçtiği için son turların kaydı burada kalıyor — el sonu ekranı ve
   * istemcinin animasyon kuyruğu bunu kullanıyor.
   */
  lastDeal: Deal | null;
}

export type Move = { type: 'play'; card: CardId };

export class IllegalMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalMoveError';
  }
}
